import { calculatePackagePricePPPW } from "@/utility/priceCalculation";
import state from "@/store/state";

function getHouseInformationCity(address) {
  if (!address || typeof address !== "object") {
    return "";
  }
  if (
    address.fullAddress &&
    typeof address.fullAddress === "object" &&
    address.fullAddress.city != null &&
    String(address.fullAddress.city).trim() !== ""
  ) {
    return String(address.fullAddress.city).trim();
  }
  if (address.city != null && String(address.city).trim() !== "") {
    return String(address.city).trim();
  }
  return "";
}

export default {
  leadType(state) {
    return state.personalDetails.leadType;
  },
  firstName(state) {
    return state.personalDetails.firstname;
  },
  lastName(state) {
    return state.personalDetails.lastname;
  },
  emailAddress(state) {
    return state.personalDetails.email;
  },
  telNumber(state) {
    return state.personalDetails.tel;
  },
  housemateCount(state) {
    return state.housemateCount;
  },
  postcode(state) {
    return state.houseInformation.postcode;
  },
  nonDiscountedPricePerPerson(state, getters) {
    return calculatePackagePricePPPW(state, getters, false);
  },
  nonDiscountedPricePerPersonPerMonth(state, getters) {
    //Convert weekly package amount from pence back to pounds
    let pppwInPounds = calculatePackagePricePPPW(state, getters, false) / 100;

    //Calculate the average price per month
    let pppmInPounds = pppwInPounds * 4.345;

    // Return back in pence for consistency
    return pppmInPounds * 100;
  },
  pricePerPerson(state, getters) {
    return calculatePackagePricePPPW(state, getters, true);
  },
  pricePerPersonPerMonth(state, getters) {
    //The monthly payment is calculated by dividing the weekly cost by 7 (days),
    //multiplying by the number of days in the year, and dividing by 12
    //(months). Part months are calculated for the number of days at the daily
    //rate.
    // The multiplier 4.34524 comes from: 365 days in a year ÷ 7 days per week ÷ 12 months
    // ≈ 4.34524 weeks in an average month.

    //Convert weekly package amount from pence back to pounds
    let pppwInPounds = calculatePackagePricePPPW(state, getters, true) / 100;

    //Calculate the average price per month
    let pppmInPounds = pppwInPounds * 4.345;

    // Return back in pence for consistency
    return pppmInPounds * 100;
  },
  formattedPricePerMonth(state, getters) {
    return (Math.ceil(getters.pricePerPersonPerMonth) / 100).toFixed(2);
  },
  selectedProducts(state) {
    if (state.accountInformation.guid === "") {
      return state.products.reduce((result, product) => {
        const productCode = state.pickedProductPriceCodes[product.type];

        let selectedOption = product.options.find((option) => {
          return option.code === productCode;
        });

        selectedOption.type = product.type;
        result.push(selectedOption);

        return result;
      }, []);
    } else {
      // Joining tenant
      return state.products;
    }
  },
  selectedUnselectedProducts(state) {
    return state.products.reduce((result, product) => {
      const productCode = state.pickedProductPriceCodes[product.type];
      const UnselectedProductCode =
        state.unpickedProductPriceCodes[product.type] || null;

      let selectedOption = product.options.find((option) => {
        return (
          option.code === productCode || option.code === UnselectedProductCode
        );
      });

      selectedOption.type = product.type;
      result.push(selectedOption);

      return result;
    }, []);
  },
  selectedProductPriceIds(state, getters) {
    return (
      getters.selectedProducts
        //.filter(product => !product.code.includes("_NONE")) Send none options to backend
        .map((product) => product.priceId)
    );
  },
  hasAddress(state) {
    const houseAddress = state.houseInformation.address;
    return (
      houseAddress.hasOwnProperty("guid") ||
      houseAddress.hasOwnProperty("fullAddress")
    );
  },
  hasAddressGuid(state) {
    const houseAddress = state.houseInformation.address;
    return houseAddress.hasOwnProperty("guid");
  },
  addressGuid(state) {
    const houseAddress = state.houseInformation.address;

    if (houseAddress.hasOwnProperty("guid")) {
      return houseAddress.guid;
    }

    return false;
  },
  fullAddress(state) {
    const houseAddress = state.houseInformation.address;

    if (houseAddress.hasOwnProperty("fullAddress")) {
      return houseAddress.fullAddress;
    }

    return false;
  },
  addressLine1(state) {
    const houseAddress = state.houseInformation.address;

    if (houseAddress.hasOwnProperty("fullAddress")) {
      return houseAddress.fullAddress.line1;
    } else if (houseAddress.hasOwnProperty("guid")) {
      return houseAddress.line1;
    }

    return null;
  },
  addressLine1And2(state) {
    const houseAddress = state.houseInformation.address;

    let address = [];
    if (houseAddress.line1) {
      address.push(houseAddress.line1);
    }

    if (houseAddress.line2) {
      address.push(houseAddress.line2);
    }

    return address.join(", ");
  },
  getCustomerAddressString(state) {
    const houseAddress = state.houseInformation.address;

    if (houseAddress.hasOwnProperty("fullAddress")) {
      // loop through full address and create string from filled values
      return Object.values(houseAddress.fullAddress)
        .filter((value) => !!value)
        .join(", ");
    } else if (houseAddress.hasOwnProperty("guid")) {
      let fullAddress = [];

      if (houseAddress.organisationName) {
        fullAddress.push(houseAddress.organisationName);
      }

      if (houseAddress.line1) {
        fullAddress.push(houseAddress.line1);
      }

      if (houseAddress.line2) {
        fullAddress.push(houseAddress.line2);
      }

      return fullAddress.join(", ");
    } else {
      return "";
    }
  },
  hasBroadband(state, getters) {
    return (
      getters.selectedProducts.filter(
        (product) =>
          product.type.toLowerCase() === "broadband" &&
          !product.code.includes("_NONE"),
      ).length > 0
    );
  },
  isLeadTenant(state, getters) {
    return !!getters.postcode;
  },
  tenancyStartDate(state) {
    let startDate = "";

    if (state.houseInformation.startDate) {
      startDate = state.houseInformation.startDate;
    } else if (state.accountInformation.tenancyStartDate) {
      startDate = state.accountInformation.tenancyStartDate;
    }

    return startDate;
  },
  tenancyEndDate(state) {
    let endDate = "";

    if (state.houseInformation.endDate) {
      endDate = state.houseInformation.endDate;
    } else if (state.accountInformation.tenancyEndDate) {
      endDate = state.accountInformation.tenancyEndDate;
    }

    return endDate;
  },
  guid(state) {
    return state.accountInformation.guid;
  },
  broadbandProduct(state) {
    if (state.accountInformation.guid === "") {
      return state.products.filter(
        (product) => product.type === "Broadband",
      )[0];
    }
    return [];
  },
  tvLicenceProduct() {
    return state.products.filter((product) => product.type === "TV Licence")[0];
  },
  defaultBroadbandProductOption(state, getters) {
    const broadbandProduct = getters.broadbandProduct;

    if (!broadbandProduct) {
      return {
        title: "Loading",
      };
    }

    let defaultOption = broadbandProduct.options.filter(
      (price) => price.code === broadbandProduct.defaultOption,
    )[0];

    if (!defaultOption) {
      defaultOption = broadbandProduct.options[0];
    }

    return defaultOption;
  },
  selectedBroadbandProductOption(state, getters) {
    return getters.broadbandProduct.options.filter(
      (price) => price.code === state.pickedProductPriceCodes["Broadband"],
    )[0];
  },
  selectedTvLicenceProductOption(state, getters) {
    return getters.tvLicenceProduct.options.filter(
      (price) => price.code === state.pickedProductPriceCodes["TV Licence"],
    )[0];
  },
  isDefaultBroadbandProductOptionSelected(state, getters) {
    // return false;
    return (
      state.pickedProductPriceCodes["Broadband"] ===
      getters.defaultBroadbandProductOption.code
    );
  },
  upgradeBroadbandProductOptions(state, getters) {
    const broadbandProduct = getters.broadbandProduct;
    const defaultOption = getters.defaultBroadbandProductOption;

    if (!broadbandProduct) {
      return {
        title: "Loading",
      };
    }

    return broadbandProduct.options.filter((product) => {
      if (product.code === defaultOption.code) {
        return false;
      }

      return !(product.code.includes("_NONE") && !getters.allowNoBroadband);
    });
  },
  canSkipBroadbandSelectionPage(state, getters) {
    return (
      getters.isDefaultBroadbandProductOptionSelected &&
      getters.upgradeBroadbandProductOptions.length === 0
    );
  },
  allowNoBroadband(state) {
    if (!state.featureFlags.hasOwnProperty("allow_no_broadband")) {
      return false;
    }

    return !!state.featureFlags.allow_no_broadband.value;
  },
  hasValidDiscount(state) {
    return state.discount.isValid;
  },
  hasUsedDiscount(state, getters) {
    return (
      getters.hasValidDiscount &&
      state.pickedProductPriceCodes[state.discount.productType] ===
        state.discount.productCode
    );
  },
  getDiscountedProductOption(state, getters) {
    if (!getters.hasUsedDiscount) {
      return null;
    }

    const product = state.products.filter(
      (product) => product.type === state.discount.productType,
    )[0];
    return product.options.find(
      (option) => option.code === state.discount.productCode,
    );
  },
  getValidationErrors: (state) => state.validationErrors,
  shouldShowVouchers(state) {
    if (!state.featureFlags.hasOwnProperty("allow_vouchers")) {
      return false;
    }

    return !!state.featureFlags.allow_vouchers.value;
  },
  getDefaultVoucher(state) {
    if (!state.featureFlags.hasOwnProperty("default_voucher")) {
      return false;
    }

    return state.featureFlags.default_voucher.value;
  },
  getAutoApplyDefaultVoucher(state) {
    if (
      !state.featureFlags.hasOwnProperty("auto_apply_default_voucher_codes")
    ) {
      return false;
    }

    return state.featureFlags["auto_apply_default_voucher_codes"].value;
  },
  getEditFlowTest(state) {
    if (!state.featureFlags.hasOwnProperty("edit_flow_test")) {
      return false;
    }

    return state.featureFlags["edit_flow_test"].value;
  },
  getShowMonthlyPriceInfo(state) {
    if (!state.featureFlags.hasOwnProperty("show_monthly_price_info")) {
      return false;
    }

    return !!state.featureFlags["show_monthly_price_info"].value;
  },
  shouldShowWhatsAppConsent(state) {
    if (!state.featureFlags.hasOwnProperty("show_whatsapp_consent")) {
      return false;
    }

    return !!state.featureFlags["show_whatsapp_consent"].value;
  },
  contentInsuranceVariation(state) {
    if (!state.featureFlags.hasOwnProperty("show_free_contents_insurance")) {
      return "control";
    }

    return state.featureFlags["show_free_contents_insurance"].value
      ? "content_insurance_enabled"
      : "control";
  },
  shouldShowFreeContentsInsurance(state) {
    if (!state.featureFlags.hasOwnProperty("show_free_contents_insurance")) {
      return false;
    }

    return !!state.featureFlags["show_free_contents_insurance"].value;
  },
  isTenancyStartDateDynamic(state) {
    if (!state.featureFlags.hasOwnProperty("dynamic_tenancy_start_date")) {
      return false;
    }

    return !!state.featureFlags.dynamic_tenancy_start_date.value;
  },
  getTenancyLengthAlert(state) {
    if (!state.featureFlags.hasOwnProperty("tenancy_length_alert")) {
      return false;
    }

    return !!state.featureFlags["tenancy_length_alert"].value;
  },
};
